import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderDark, setIsHeaderDark] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
      setIsHeaderDark(scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "dot Blue",
    "url": siteUrl,
    "logo": "https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/5a08c197f8230028d9ecca8183376b65.png",
    "description": "導入だけで終わらせず、業務フローへの「埋め込み」と「運用」で成果を出すAI×DX支援企業。経営課題起点で設計し、実務で使えるAIシステムを構築・内製化支援します。",
    "areaServed": "JP"
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "生成AI活用・DX実装の伴走パートナー｜dot Blue",
    "description": "導入だけで終わらせず、業務フローへの「埋め込み」と「運用」で成果を出すAI×DX支援企業。経営課題起点で設計し、実務で使えるAIシステムを構築・内製化支援します。",
    "url": siteUrl,
    "publisher": {
      "@type": "Organization",
      "name": "dot Blue",
      "url": siteUrl
    }
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "AI×DX実装支援サービス",
    "description": "PoC、受託開発、UX設計、コンバージョン改善、運用改善、採用AI、AIチャット、検索、レコメンド、エージェント",
    "provider": {
      "@type": "Organization",
      "name": "dot Blue",
      "url": siteUrl
    },
    "areaServed": "JP"
  };

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [webPageSchema, organizationSchema, serviceSchema]
  };

  return (
    <>
      <SEO
        title="生成AI活用・DX実装の伴走パートナー｜dot Blue"
        description="導入だけで終わらせず、業務フローへの「埋め込み」と「運用」で成果を出すAI×DX支援企業。経営課題起点で設計し、実務で使えるAIシステムを構築・内製化支援します。"
        keywords="dot Blue, AI実装, AI運用, PoC, 受託開発, UX設計, コンバージョン改善, 運用改善, 採用AI, AIチャット, 検索, レコメンド, エージェント"
        ogImage={`${siteUrl}/og-image-home.jpg`}
        schema={combinedSchema}
      />
      <div className="min-h-screen bg-white">
        {/* Header - Global Navigation */}
        <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300" style={{ background: isHeaderDark ? 'rgba(255, 255, 255, 0.95)' : 'transparent', backdropFilter: isHeaderDark ? 'blur(10px)' : 'none', boxShadow: isHeaderDark ? '0 2px 10px rgba(0,0,0,0.05)' : 'none' }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-center justify-between h-20">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/5a08c197f8230028d9ecca8183376b65.png"
                  alt="dot Blue"
                  className="h-16 w-auto drop-shadow-md"
                />
              </Link>
              
              {/* Center Navigation */}
              <nav className="hidden lg:flex items-center gap-8">
                <Link
                  to="/"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33] border-b-2 border-[#2563EB]"
                >
                  TOP
                </Link>
                <Link
                  to="/ai-insider"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33]/70 hover:text-[#0B1B33]"
                >
                  AI Insider
                </Link>
                <Link
                  to="/ai-development"
                  className="text-sm font-bold transition-all whitespace-nowrap cursor-pointer drop-shadow-sm text-[#0B1B33]/70 hover:text-[#0B1B33]"
                >
                  AI×DX開発
                </Link>
              </nav>

              {/* CTA Button */}
              <div className="hidden lg:flex items-center">
                <button
                  onClick={() => scrollToSection('contact')}
                  className="px-6 py-2.5 bg-gradient-to-r from-[#FF9900] to-[#FF7700] text-white text-sm font-semibold rounded-lg hover:from-[#E68A00] hover:to-[#E67700] transition-all whitespace-nowrap cursor-pointer shadow-[0_4px_16px_rgba(255,153,0,0.3)] hover:shadow-[0_6px_24px_rgba(255,153,0,0.4)] focus:ring-2 focus:ring-[#FF9900]/30 focus:outline-none"
                >
                  無料で相談する
                </button>
              </div>

              {/* Mobile Menu */}
              <div className="lg:hidden">
                <div className="flex gap-4">
                  <Link
                    to="/"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#0B1B33] border-b-2 border-[#2563EB]"
                  >
                    TOP
                  </Link>
                  <Link
                    to="/ai-insider"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#6B7280]"
                  >
                    AI Insider
                  </Link>
                  <Link
                    to="/ai-development"
                    className="text-xs font-medium transition-colors whitespace-nowrap pb-1 cursor-pointer text-[#6B7280]"
                  >
                    AI×DX開発
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section - Main Visual (FV) */}
        <section id="top" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-[0.03]">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#2563EB" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          {/* Geometric Shapes */}
          <div className="absolute top-20 right-10 w-32 h-32 lg:w-64 lg:h-64 bg-gradient-to-br from-[#2563EB]/10 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-48 h-48 lg:w-96 lg:h-96 bg-gradient-to-tr from-[#FF9900]/10 to-transparent rounded-full blur-3xl"></div>

          {/* Background Visual - Right Side */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/2 h-full opacity-20">
            <img 
              src="https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/86b24e2ce5cee7a65c40ff6f4b00b25a.png"
              alt=""
              className="w-full h-full object-cover object-center mix-blend-multiply"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#2563EB]/20 to-white border-none"></div>
          </div>

          {/* Light Rays */}
          <div className="absolute top-1/2 right-0 w-full h-1 bg-gradient-to-l from-[#2563EB]/30 via-[#2563EB]/10 to-transparent blur-sm"></div>
          <div className="absolute top-1/2 right-0 w-full h-1 bg-gradient-to-l from-[#2563EB]/20 via-[#2563EB]/5 to-transparent blur-md translate-y-8"></div>

          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 relative z-10 pt-20">
            <div className="max-w-4xl">
              {/* Subtitle */}
              <p className="text-xs font-semibold text-[#2563EB] mb-4 tracking-[0.3em] uppercase">
                DESIGNING DECISION MAKING
              </p>

              {/* Main Copy */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-black text-[#0B1B33] mb-8 leading-[1.1] tracking-tight" style={{ textShadow: '0 0 40px rgba(37, 99, 235, 0.15)' }}>
                意思決定を<br />
                デザインする
              </h1>

              {/* Lead Text */}
              <p className="text-base sm:text-lg lg:text-xl text-[#555555] mb-12 leading-relaxed max-w-2xl" style={{ lineHeight: '1.8' }}>
                「決めきれない」ことで止まりがちなユーザーや組織の意思決定を<br className="hidden sm:block" />
                AIで確かな行動へ。<br />
                PoCから本番運用まで、課題起点で伴走します。
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('services')}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-transparent border-2 border-[#0B1B33] text-[#0B1B33] text-sm sm:text-base font-semibold rounded-lg hover:bg-[#0B1B33] hover:text-white transition-all whitespace-nowrap cursor-pointer shadow-md hover:shadow-xl focus:ring-2 focus:ring-[#0B1B33]/30 focus:outline-none"
                >
                  サービス一覧を見る
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#FF9900] to-[#FF7700] text-white text-sm sm:text-base font-semibold rounded-lg hover:from-[#E68A00] hover:to-[#E67700] transition-all whitespace-nowrap cursor-pointer shadow-[0_8px_24px_rgba(255,153,0,0.3)] hover:shadow-[0_12px_32px_rgba(255,153,0,0.4)] focus:ring-2 focus:ring-[#FF9900]/30 focus:outline-none"
                >
                  無料で相談する
                </button>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
            <p className="text-xs font-medium text-[#0B1B33]/50 tracking-[0.2em]">SCROLL DOWN</p>
            <i className="ri-arrow-down-line text-2xl text-[#0B1B33]/50"></i>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-12 sm:py-16 px-4 sm:px-6 relative overflow-hidden bg-[#F5F5F2]">
          {/* Background circles */}
          <div className="absolute top-0 left-0 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] rounded-full bg-[#EAF2FF] opacity-10 blur-3xl -translate-x-1/3 -translate-y-1/3"></div>
          <div className="absolute bottom-0 right-0 w-[150px] sm:w-[250px] h-[150px] sm:h-[250px] rounded-full bg-[#EAF2FF] opacity-8 blur-3xl translate-x-1/4 translate-y-1/4"></div>
          <div className="absolute top-1/2 left-1/2 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full bg-[#DBEAFE] opacity-20 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-8 sm:mb-10">
              <p className="text-xs font-semibold text-[#2563EB] mb-2 tracking-[0.2em] uppercase">About</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0B0B0C] tracking-tight">私たちについて</h2>
            </div>
            
            <div className="space-y-4 sm:space-y-5 text-sm leading-relaxed text-[#374151]" style={{ lineHeight: '1.8' }}>
              <p>
                AIの普及は、皮肉にも世界の均質化を加速させています。
              </p>
              
              <p>
                企業という存在も例外ではありません。しかし、企業の本質は「他と異なろう」という意志と活動にあります。これからの時代、企業はこれまで以上に「ユニークネスは何か？」と問われることになるはずです。
              </p>
              
              <p>
                その中で私たちは、AIを<span className="font-bold text-[#0B0B0C]">「企業のユニークネスを創る資本」</span>と再定義します。
              </p>
              
              <p>
                企業の独自性・競争力・実行力を押し上げるために、AIを活用していく。これが私たちの考えるAIのあるべき活用の姿です。
              </p>
              
              <p>
                もちろん、思想だけではビジネスは動きません。
              </p>
              
              <p>
                私たちがこだわるのは<span className="font-bold text-[#0B0B0C]">「AIによってビジネスが前に進んだか」</span>です。
              </p>
              
              <p>
                技術的な支援だけでなく、ビジネスにおける<span className="font-bold text-[#0B0B0C]">「インパクト」</span>を重視し、現場で実際にワークするシステムを実装します。
              </p>
              
              <p>
                テクノロジーとビジネスの知見を掛け合わせ、組織と個人の<span className="font-bold text-[#0B0B0C]">意思決定をデザインする。<br className="hidden sm:block" /></span>貴社のユニークネスを創り、事業が成長し続けるその時まで、私たちは全力で伴走します。
              </p>
            </div>
          </div>
        </section>

        {/* Strengths Section */}
        <section id="strengths" className="py-12 sm:py-16 px-4 sm:px-6 bg-[#F5F5F2] relative overflow-hidden">
          {/* Background circle */}
          <div className="absolute top-1/2 right-0 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] rounded-full bg-[#EAF2FF] opacity-10 blur-3xl translate-x-1/4 -translate-y-1/2"></div>
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-8 sm:mb-10">
              <p className="text-xs font-semibold text-[#2563EB] mb-2 tracking-[0.2em] uppercase">Strengths</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0B0B0C] tracking-tight">私たちの強み</h2>
            </div>
            
            <div className="text-center mb-6 sm:mb-8">
              <p className="text-base sm:text-lg font-bold text-[#0B0B0C] leading-relaxed px-4" style={{ lineHeight: '1.7' }}>
                ツールの開発だけではありません。<br />
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
              <div className="bg-white p-5 sm:p-6 rounded-xl border border-[#E5E7EB] shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-briefcase-line text-xl text-[#2563EB]"></i>
                  </div>
                </div>
                <h3 className="text-sm font-bold text-[#0B0B0C] mb-3 text-center">経営視点での徹底伴走</h3>
                <p className="text-sm text-[#374151] leading-relaxed text-center" style={{ lineHeight: '1.7' }}>
                  ビジネスゴールから逆算して設計。社内合意形成など、推進プロセスまでBizサイドがリード。
                </p>
              </div>
              
              <div className="bg-white p-5 sm:p-6 rounded-xl border border-[#E5E7EB] shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-code-box-line text-xl text-[#2563EB]"></i>
                  </div>
                </div>
                <h3 className="text-sm font-bold text-[#0B0B0C] mb-3 text-center">研究×開発のプロによる実装力</h3>
                <p className="text-sm text-[#374151] leading-relaxed text-center" style={{ lineHeight: '1.7' }}>
                  課題起点の設計と確かな技術力。PoC（実証）で終わらせず、本番運用に耐えうるシステムを構築。
                </p>
              </div>
              
              <div className="bg-white p-5 sm:p-6 rounded-xl border border-[#E5E7EB] shadow-[0_2px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-image-line text-xl text-[#2563EB]"></i>
                  </div>
                </div>
                <h3 className="text-sm font-bold text-[#0B0B0C] mb-3 text-center">成果にコミットする「運用伴走」</h3>
                <p className="text-sm text-[#374151] leading-relaxed text-center" style={{ lineHeight: '1.7' }}>
                  リリースしてからが本番。「現場での定着」「数値の改善」をウォッチし、泥臭く改善し続けます。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-12 sm:py-16 px-4 sm:px-6 bg-[#F7F7F5] relative overflow-hidden">
          {/* Background circle */}
          <div className="absolute top-0 left-0 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] rounded-full bg-[#EAF2FF] opacity-8 blur-3xl -translate-x-1/3"></div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-8 sm:mb-10">
              <p className="text-xs font-semibold text-[#2563EB] mb-2 tracking-[0.2em] uppercase">Services</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0B0B0C] tracking-tight">サービス</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Card 1: AI Insider */}
              <div className="bg-white rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-all border border-[#E5E7EB] flex flex-col h-full">
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/464dfa1e7b84c34c92ef38c7ffade884.png"
                    alt="AI Insider"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-5 sm:p-6 flex flex-col flex-grow">
                  <div className="mb-3">
                    <span className="text-[0.65rem] font-medium text-[#555555] bg-[#F0F2F5] px-2 py-1 rounded">SaaS / HR Tech</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0B0B0C] mb-3">対話型採用AI「AI Insider」</h3>
                  <p className="text-sm text-[#374151] leading-relaxed mb-6 flex-grow" style={{ lineHeight: '1.7' }}>
                    候補者の本音を引き出し、動機形成を行う採用特化型の対話AI。24時間365日、採用担当者に代わって候補者に寄り添い、歩留まりを向上させます。
                  </p>
                  <Link
                    to="/ai-insider"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-[#3B5BDB] text-[#3B5BDB] text-sm font-semibold rounded hover:bg-[#3B5BDB]/5 transition-all cursor-pointer group"
                  >
                    プロダクトサイトを見る
                    <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </div>

              {/* Card 2: AI Solution Development */}
              <div className="bg-white rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-all border border-[#E5E7EB] flex flex-col h-full">
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Structured%20code%20blocks%20and%20organized%20programming%20architecture%20visualization%2C%20clean%20geometric%20patterns%20representing%20software%20development%2C%20deep%20blue%20tones%20with%20minimalist%20design%2C%20professional%20technology%20illustration%20showing%20systematic%20structure%2C%20modern%20abstract%20representation%20of%20coding%20and%20algorithms&width=800&height=450&seq=aisolution-service&orientation=landscape"
                    alt="AI×DX システム実装・刷新"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-5 sm:p-6 flex flex-col flex-grow">
                  <div className="mb-3">
                    <span className="text-[0.65rem] font-medium text-[#555555] bg-[#F0F2F5] px-2 py-1 rounded">受託開発 / SI</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0B0B0C] mb-3">AI×DX システム実装・刷新</h3>
                  <p className="text-sm text-[#374151] leading-relaxed mb-6 flex-grow" style={{ lineHeight: '1.7' }}>
                    UX設計からシステム実装、データ整備、ガバナンス構築まで。業務フローを変革するオーダーメイドのAIシステムを開発します。
                  </p>
                  <Link
                    to="/ai-development"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-[#3B5BDB] text-[#3B5BDB] text-sm font-semibold rounded hover:bg-[#3B5BDB]/5 transition-all cursor-pointer group"
                  >
                    サービス詳細を見る
                    <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
                  </Link>
                </div>
              </div>

              {/* Card 3: AI Consulting */}
              <div className="bg-white rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] transition-all border border-[#E5E7EB] flex flex-col h-full">
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src="https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/00142ebc4dbc99c88694deda404e3838.png"
                    alt="AI導入コンサルティング"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-5 sm:p-6 flex flex-col flex-grow">
                  <div className="mb-3">
                    <span className="text-[0.65rem] font-medium text-[#555555] bg-[#F0F2F5] px-2 py-1 rounded">戦略 / 人材育成 / 伴走支援</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0B0B0C] mb-3">AI導入コンサルティング</h3>
                  <p className="text-sm text-[#374151] leading-relaxed mb-6 flex-grow" style={{ lineHeight: '1.7' }}>
                    「AIで何ができるか」の戦略策定から、ROI試算、ロードマップ作成を支援。社内人材の育成（研修）や組織定着まで、泥臭く伴走します。
                  </p>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-[#3B5BDB] text-[#3B5BDB] text-sm font-semibold rounded hover:bg-[#3B5BDB]/5 transition-all cursor-pointer group"
                  >
                    活用について相談する
                    <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="py-12 sm:py-16 px-4 sm:px-6 bg-[#F5F5F2] relative overflow-hidden">
          {/* Background circle */}
          <div className="absolute bottom-0 left-0 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] rounded-full bg-[#EAF2FF] opacity-10 blur-3xl -translate-x-1/4 translate-y-1/4"></div>
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-8 sm:mb-10">
              <p className="text-xs font-semibold text-[#2563EB] mb-2 tracking-[0.2em] uppercase">Pricing</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0B0B0C] mb-4 tracking-tight">料金</h2>
              <p className="text-base font-bold text-[#0B0B0C] leading-relaxed px-4">
                案件ごとにお見積もりします
              </p>
              <p className="text-xs text-[#6B7280] mt-2">
                (目的・範囲・運用体制によって変わります)
              </p>
            </div>

            {/* Process Flow with Cards */}
            <div className="flex flex-col lg:flex-row items-center justify-center gap-4 sm:gap-6 lg:gap-4 max-w-5xl mx-auto">
              {/* Step 1 */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 min-h-[200px] flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-[#E5E7EB] flex-1">
                <div className="text-xs font-bold text-[#2563EB] mb-3 tracking-[0.2em]">STEP 01</div>
                <h3 className="text-lg font-bold text-[#0B0B0C] mb-4">診断・ROI設計</h3>
                <p className="text-sm text-[#374151] leading-relaxed" style={{ lineHeight: '1.7' }}>
                  現場課題の棚卸しと適用領域の選定を行い、投資対効果（ROI）とガバナンスルールを定義します。
                </p>
              </div>

              {/* Arrow 1 */}
              <div className="hidden lg:flex items-center justify-center">
                <i className="ri-arrow-right-line text-4xl text-[#2563EB] font-bold"></i>
              </div>

              {/* Step 2 */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 min-h-[200px] flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-[#E5E7EB] flex-1">
                <div className="text-xs font-bold text-[#2563EB] mb-3 tracking-[0.2em]">STEP 02</div>
                <h3 className="text-lg font-bold text-[#0B0B0C] mb-4">業務実装・統合</h3>
                <p className="text-sm text-[#374151] leading-relaxed" style={{ lineHeight: '1.7' }}>
                  単体ツールではなく、既存システムや業務フローに深く組み込んだ「現場が使いやすいAI」を構築します。
                </p>
              </div>

              {/* Arrow 2 */}
              <div className="hidden lg:flex items-center justify-center">
                <i className="ri-arrow-right-line text-4xl text-[#2563EB] font-bold"></i>
              </div>

              {/* Step 3 */}
              <div className="bg-white rounded-2xl p-6 sm:p-8 min-h-[200px] flex flex-col items-center justify-center text-center shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-[#E5E7EB] flex-1">
                <div className="text-xs font-bold text-[#2563EB] mb-3 tracking-[0.2em]">STEP 03</div>
                <h3 className="text-lg font-bold text-[#0B0B0C] mb-4">運用・改善(DevOps)</h3>
                <p className="text-sm text-[#374151] leading-relaxed" style={{ lineHeight: '1.7' }}>
                  利用ログを分析し、回答精度とUXを継続的に改善して成果を最大化させます。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Team Section */}
        <section id="team" className="py-12 sm:py-16 px-4 sm:px-6 bg-[#F5F5F2] relative overflow-hidden">
          {/* Background circle */}
          <div className="absolute top-1/2 right-0 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] rounded-full bg-[#EAF2FF] opacity-8 blur-3xl translate-x-1/3 -translate-y-1/2"></div>
          
          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-8 sm:mb-10">
              <p className="text-xs font-semibold text-[#2563EB] mb-2 tracking-[0.2em] uppercase">Our team</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0B0B0C] tracking-tight">チーム</h2>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Team Member 1 */}
              <div className="bg-white rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-[#E5E7EB] flex flex-col sm:flex-row">
                <div className="sm:w-[30%] aspect-[3/4] sm:aspect-auto overflow-hidden flex-shrink-0">
                  <img 
                    src="https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/6c3d6900550dfc15afc03c617d26adad.jpeg"
                    alt="平舘 慶大"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="sm:w-[70%] p-5 sm:p-6 flex flex-col justify-center">
                  <h3 className="text-lg font-bold mb-1 text-[#0B0B0C]">平舘 慶大</h3>
                  <p className="text-[#6B7280] font-medium mb-3 text-xs">CEO / Biz</p>
                  <p className="text-[#374151] leading-relaxed text-sm" style={{ lineHeight: '1.7' }}>
                    国際基督教大学卒業後、味の素㈱へ入社。営業と国内/海外マーケの傍ら、AIなど複数スタートアップにも参画。退職後、AIの社会実装を志し、25年に㈱dot Blueを創業。
                  </p>
                </div>
              </div>

              {/* Team Member 2 */}
              <div className="bg-white rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] border border-[#E5E7EB] flex flex-col sm:flex-row">
                <div className="sm:w-[30%] aspect-[3/4] sm:aspect-auto overflow-hidden flex-shrink-0">
                  <img 
                    src="https://static.readdy.ai/image/9d74872511a6806a1ddbaf05c38ab433/622843521d7f0302ecf2658dddcfa6ee.jpeg"
                    alt="藤岡 拓也"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="sm:w-[70%] p-5 sm:p-6 flex flex-col justify-center">
                  <h3 className="text-lg font-bold mb-1 text-[#0B0B0C]">藤岡 拓也</h3>
                  <p className="text-[#6B7280] font-medium mb-3 text-xs">CTO / Tech</p>
                  <p className="text-[#374151] leading-relaxed text-sm" style={{ lineHeight: '1.7' }}>
                    九州大学修士課程修了。日立製作所にて、音声AI領域の研究開発と新規事業創出に従事。2023年に独立。AIプロダクトの企画・実装からPMまでを伴走支援。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-12 sm:py-16 px-4 sm:px-6 bg-[#F5F5F2] relative overflow-hidden">
          {/* Background circle */}
          <div className="absolute top-0 left-1/2 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] rounded-full bg-[#EAF2FF] opacity-10 blur-3xl -translate-x-1/2 -translate-y-1/3"></div>
          
          <div className="max-w-2xl mx-auto relative z-10">
            <div className="text-center mb-6 sm:mb-8">
              <p className="text-xs font-semibold text-[#2563EB] mb-2 tracking-[0.2em] uppercase">Contact</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0B0B0C] tracking-tight">お問い合わせ</h2>
            </div>
            
            <p className="text-center text-sm text-[#6B7280] mb-6">
              ご相談内容をお送りください。
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-[#0B0B0C] mb-1.5">
                  会社名
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[rgba(37,99,235,0.2)] focus:border-[#2563EB] text-sm bg-white transition-all"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#0B0B0C] mb-1.5">
                  お名前
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[rgba(37,99,235,0.2)] focus:border-[#2563EB] text-sm bg-white transition-all"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#0B0B0C] mb-1.5">
                  メール
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[rgba(37,99,235,0.2)] focus:border-[#2563EB] text-sm bg-white transition-all"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#0B0B0C] mb-1.5">
                  相談内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  required
                  className="w-full px-3 py-2.5 border border-[#D1D5DB] rounded-lg focus:ring-2 focus:ring-[rgba(37,99,235,0.2)] focus:border-[#2563EB] resize-none text-sm bg-white transition-all"
                ></textarea>
              </div>

              {formStatus === 'success' && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
                  送信が完了しました。2営業日以内にご返信いたします。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                  送信に失敗しました。もう一度お試しください。
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-6 py-3 bg-[#FF9900] text-white text-sm font-semibold rounded-lg hover:bg-[#E68A00] transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_2px_8px_rgba(255,153,0,0.2)] hover:shadow-[0_4px_12px_rgba(255,153,0,0.3)] focus:ring-2 focus:ring-[rgba(255,153,0,0.35)] focus:outline-none"
              >
                {formStatus === 'submitting' ? '送信中...' : '無料で相談する'}
              </button>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#0B0B0C] text-white py-8 sm:py-10 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-5 sm:mb-6">
              <h3 className="text-xs font-semibold text-[#E5E7EB] mb-3">リンク</h3>
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <a href="/privacy-policy" className="text-xs text-[#9CA3AF] hover:text-white transition-colors cursor-pointer hover:underline">
                  プライバシーポリシー
                </a>
                <Link to="/terms" className="text-xs text-[#9CA3AF] hover:text-white transition-colors cursor-pointer hover:underline">
                  利用規約
                </Link>
                <Link to="/security" className="text-xs text-[#9CA3AF] hover:text-white transition-colors cursor-pointer hover:underline">
                  セキュリティ
                </Link>
                <Link to="/company" className="text-xs text-[#9CA3AF] hover:text-white transition-colors cursor-pointer hover:underline">
                  会社概要
                </Link>
              </div>
            </div>
            
            <div className="pt-5 sm:pt-6 border-t border-[#374151] flex flex-col lg:flex-row justify-between items-center gap-3">
              <p className="text-xs text-[#9CA3AF]">Copyright © dot Blue Inc. All Rights Reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
